import React, { useEffect, useState } from 'react';
import {
  Box, Button, CircularProgress, Table, TableBody, TableCell, TableHead,
  TableRow, Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useIsAdmin } from './useIsAdmin';
import { adminUrl } from './api';
import { Metrics, RenderRow } from './types';
import { formatChartWeek } from './HistoryTable';

const PT_FORMAT: Intl.DateTimeFormatOptions = {
  timeZone: 'America/Los_Angeles',
  year: 'numeric', month: 'short', day: 'numeric',
  hour: 'numeric', minute: '2-digit',
};

/**
 * Clean, client-facing report for the selected range — print to PDF via the
 * browser. Buttons are hidden when printing.
 */
export const PrintReport: React.FC = () => {
  const { isAdmin, loading: authLoading } = useIsAdmin();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const [renders, setRenders] = useState<RenderRow[] | null>(null);
  const [metrics, setMetrics] = useState<Metrics | null>(null);

  const from = search.get('from');
  const to = search.get('to');

  useEffect(() => {
    if (authLoading || !isAdmin) return;
    const params = {
      from, to,
      chart: search.get('chart'),
      orientation: search.get('orientation'),
      language: search.get('language'),
      dateBasis: search.get('dateBasis'),
    };
    Promise.all([
      fetch(adminUrl('renders', { ...params, includeReruns: true })).then((r) => r.json()),
      fetch(adminUrl('metrics', params)).then((r) => r.json()),
    ]).then(([r, m]) => {
      setRenders(r.renders || []);
      setMetrics(m);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAdmin, search]);

  if (authLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (!isAdmin) return <Navigate to="/" replace />;
  if (!renders || !metrics) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  const basisLabel = search.get('dateBasis') === 'rendered' ? 'by run date' : 'by chart date';
  const rangeLabel = from || to
    ? `${from || 'Beginning'} through ${to || 'today'} (${basisLabel})`
    : 'All time';

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', py: 3 }}>
      <Box sx={{ display: 'flex', gap: 1, mb: 2, '@media print': { display: 'none' } }}>
        <Button startIcon={<ArrowBackIcon />} size="small" onClick={() => navigate('/dashboard')}>
          Back to dashboard
        </Button>
        <Button startIcon={<PrintIcon />} variant="contained" size="small" onClick={() => window.print()}>
          Print / Save as PDF
        </Button>
      </Box>

      <Typography variant="h5" sx={{ fontWeight: 700 }}>Chart Reveal — Rendered Versions Report</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {rangeLabel} · Generated {new Date().toLocaleDateString('en-US', { timeZone: 'America/Los_Angeles', dateStyle: 'long' })} · All times Pacific
      </Typography>

      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Summary</Typography>
      <Table size="small" sx={{ maxWidth: 420, mb: 2 }}>
        <TableBody>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Billable versions</TableCell>
            <TableCell align="right">{metrics.totals.billable}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Reruns (not billed)</TableCell>
            <TableCell align="right">{metrics.totals.reruns}</TableCell>
          </TableRow>
          {metrics.byChart.map((b) => (
            <React.Fragment key={b.key}>
              <TableRow>
                <TableCell sx={{ pl: 3, fontWeight: 600 }}>{b.key}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{b.billable}</TableCell>
              </TableRow>
              {(metrics.byVersion || [])
                .filter((v) => v.chart === b.key && (v.billable > 0 || v.reruns > 0))
                .map((v) => (
                  <TableRow key={`${v.chart}|${v.orientation}|${v.language}`}>
                    <TableCell sx={{ pl: 6, color: 'text.secondary' }}>
                      {v.orientation} · {(v.language || 'en').toUpperCase()}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'text.secondary' }}>{v.billable}</TableCell>
                  </TableRow>
                ))}
            </React.Fragment>
          ))}
        </TableBody>
      </Table>

      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>All renders</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Rendered (PT)</TableCell>
            <TableCell>Chart</TableCell>
            <TableCell>Chart week</TableCell>
            <TableCell>Orientation</TableCell>
            <TableCell>Language</TableCell>
            <TableCell>Billable</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {renders.map((r) => (
            <TableRow key={r.id}>
              <TableCell>{r.renderedAt ? new Date(r.renderedAt).toLocaleString('en-US', PT_FORMAT) : '—'}</TableCell>
              <TableCell>{r.chart}</TableCell>
              <TableCell>{formatChartWeek(r.chartWeek)}</TableCell>
              <TableCell>{r.orientation}</TableCell>
              <TableCell>{(r.language || 'en').toUpperCase()}</TableCell>
              <TableCell>{r.billable ? 'Yes' : (r.isRerun ? 'No (rerun)' : 'No')}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};
